import { type FormEvent, useEffect, useMemo, useState } from 'react';

import { useAuth, usePortalSession } from '../auth';
import {
  getPortalEventGuestInvitations,
  invitePortalEventGuest,
  PortalApiError,
  updatePortalEventGuestInvitation,
} from '../services/portalApi';
import type {
  GuestInvitationInput,
  PortalEvent,
  PortalEventRole,
  PortalGuestInvitation,
} from '../types/api';
import { CompactTable } from './CompactTable';

interface GuestInvitationPanelProps {
  events: PortalEvent[];
  assignableRoles: PortalEventRole[];
}

const emptyInvitation: GuestInvitationInput = {
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '+47',
  organization: '',
  role: 'Caller',
};

export function GuestInvitationPanel({
  events,
  assignableRoles,
}: GuestInvitationPanelProps) {
  const { getAccessToken } = useAuth();
  const { user } = usePortalSession();
  const [selectedEventId, setSelectedEventId] = useState('');
  const [invitation, setInvitation] = useState<GuestInvitationInput>(emptyInvitation);
  const [pendingInvitations, setPendingInvitations] = useState<PortalGuestInvitation[]>([]);
  const [editing, setEditing] = useState<PortalGuestInvitation | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const eventIds = events.map((event) => event.EventId).join('|');

  const selectedEvent = useMemo(
    () => events.find((event) => event.EventId === selectedEventId)
      ?? events[0]
      ?? null,
    [events, selectedEventId],
  );

  useEffect(() => {
    if (!eventIds) return;
    let cancelled = false;

    async function loadPendingInvitations(): Promise<void> {
      try {
        const token = await getAccessToken();
        const responses = await Promise.all(eventIds.split('|').map(
          (eventId) => getPortalEventGuestInvitations(token, eventId),
        ));
        if (!cancelled) {
          setPendingInvitations(responses.flatMap((response) => response.invitations));
        }
      } catch (error) {
        if (!cancelled) {
          setIsError(true);
          setMessage(toMessage(error));
        }
      }
    }

    void loadPendingInvitations();
    return () => { cancelled = true; };
  }, [eventIds, getAccessToken]);

  async function refreshPendingInvitations(): Promise<void> {
    if (!eventIds) return;
    const token = await getAccessToken();
    const responses = await Promise.all(eventIds.split('|').map(
      (eventId) => getPortalEventGuestInvitations(token, eventId),
    ));
    setPendingInvitations(responses.flatMap((response) => response.invitations));
  }

  async function sendInvitation(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedEvent) return;

    setIsBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      const token = await getAccessToken();
      const response = await invitePortalEventGuest(
        token,
        selectedEvent.EventId,
        invitation,
      );
      setMessage(`Email and SMS invitation sent to ${response.invitation.Email}.`);
      setInvitation(emptyInvitation);
      await refreshPendingInvitations();
    } catch (error) {
      setIsError(true);
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  function beginEdit(item: PortalGuestInvitation): void {
    setEditing(item);
    setInvitation({
      firstName: item.FirstName,
      lastName: item.LastName,
      email: item.Email,
      mobileNumber: item.MobileNumber,
      organization: item.Organization,
      role: item.Role,
    });
    setSelectedEventId(item.EventId);
    setMessage(null);
  }

  function cancelEdit(): void {
    setEditing(null);
    setInvitation(emptyInvitation);
  }

  async function saveInvitation(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!editing) return;
    setIsBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      const token = await getAccessToken();
      await updatePortalEventGuestInvitation(
        token,
        editing.EventId,
        editing.InvitationId,
        invitation,
      );
      setEditing(null);
      setInvitation(emptyInvitation);
      setMessage('The invitation was updated. A new email and SMS code were sent.');
      await refreshPendingInvitations();
    } catch (error) {
      setIsError(true);
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  if (!selectedEvent || assignableRoles.length === 0) return null;

  return (
    <>
      <section className="guest-invitation-section">
        <div>
          <p className="eyebrow">External participant</p>
          <h2>{editing ? 'Edit pending invitation' : 'Invite external participant'}</h2>
          <p className="form-description">
            The participant receives an email link and a verification code by SMS.
          </p>
          <form className="guest-invitation-form"
            onSubmit={(event) => void (editing
              ? saveInvitation(event)
              : sendInvitation(event))}>
            {events.length > 1 ? (
              <label>
                Event
                <select value={selectedEvent.EventId} disabled={Boolean(editing)}
                  onChange={(event) => setSelectedEventId(event.target.value)}>
                  {events.map((event) => (
                    <option key={event.EventId} value={event.EventId}>{event.Name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="guest-name-fields">
              <TextField label="First name" value={invitation.firstName}
                onChange={(value) => setInvitation({ ...invitation, firstName: value })} />
              <TextField label="Last name" value={invitation.lastName}
                onChange={(value) => setInvitation({ ...invitation, lastName: value })} />
            </div>
            <label>
              Email
              <input required type="email" maxLength={320} value={invitation.email}
                onChange={(event) => setInvitation({ ...invitation, email: event.target.value })} />
            </label>
            <label>
              Mobile number
              <input required type="tel" placeholder="+4712345678"
                pattern="\+[1-9][0-9]{7,14}" value={invitation.mobileNumber}
                onChange={(event) => setInvitation({
                  ...invitation, mobileNumber: event.target.value,
                })} />
            </label>
            <TextField label="Organization" value={invitation.organization}
              maxLength={200}
              onChange={(value) => setInvitation({ ...invitation, organization: value })} />
            <label>
              Event role
              <select value={invitation.role}
                onChange={(event) => setInvitation({
                  ...invitation, role: event.target.value as PortalEventRole,
                })}>
                {assignableRoles.map((role) => (
                  <option key={role} value={role}>{formatRole(role)}</option>
                ))}
              </select>
            </label>
            <div className="event-form-actions">
              <button className="primary-button" type="submit" disabled={isBusy}>
                {isBusy ? 'Sending…' : editing ? 'Save and resend' : 'Send invitation'}
              </button>
              {editing ? (
                <button className="secondary-button" type="button"
                  disabled={isBusy} onClick={cancelEdit}>Cancel</button>
              ) : null}
            </div>
          </form>
        </div>

        <aside className="invitation-preview">
          <p className="eyebrow">Invitation preview</p>
          <h3>Welcome to the YWAM Norway calling portal.</h3>
          <p>
            {user?.displayName ?? 'The Event Manager'} has invited{' '}
            {invitation.firstName || 'this participant'} to be a{' '}
            {formatRole(invitation.role)} for {selectedEvent.Name}.
          </p>
          {selectedEvent.Description ? <p>{selectedEvent.Description}</p> : null}
          <p>The email contains the acceptance link. A six-digit code is sent by SMS.</p>
        </aside>
      </section>

      {message ? (
        <p className={isError ? 'api-message' : 'success-message'}
          role={isError ? 'alert' : 'status'}>{message}</p>
      ) : null}

      {pendingInvitations.length > 0 ? (
        <section className="pending-invitations-section">
          <h2>Pending invitations</h2>
          <CompactTable label="Pending invitations" headers={[
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
            { key: 'mobile', label: 'Mobile' },
            ...(events.length > 1 ? [{ key: 'event', label: 'Event' }] : []),
            { key: 'role', label: 'Role' },
            { key: 'status', label: 'Status' },
            { key: 'actions', label: '' },
          ]}>
            {pendingInvitations.map((item) => (
              <tr key={item.InvitationId}>
                <td>{[item.FirstName, item.LastName].filter(Boolean).join(' ')}</td>
                <td>{item.Email}</td>
                <td>{item.MobileNumber}</td>
                {events.length > 1 ? <td>{item.EventName}</td> : null}
                <td>{formatRole(item.Role)}</td>
                <td><span className={item.Status === 'Failed'
                  ? 'user-status user-status-blocked'
                  : 'user-status user-status-pending'}>
                  {item.Status === 'Failed' ? 'Delivery failed' : 'Invitation pending'}
                </span></td>
                <td className="compact-table-actions">
                  <button className="table-action-button" type="button"
                    onClick={() => beginEdit(item)}>Edit</button>
                </td>
              </tr>
            ))}
          </CompactTable>
        </section>
      ) : null}
    </>
  );
}

function TextField({ label, value, onChange, maxLength = 100 }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
}) {
  return (
    <label>
      {label}
      <input required maxLength={maxLength} value={value}
        onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function formatRole(role: PortalEventRole): string {
  if (role === 'EventManager') return 'Event Manager';
  if (role === 'CallLead') return 'Call Lead';
  return 'Caller';
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError || error instanceof Error) return error.message;
  return 'The invitation request could not be completed.';
}
